import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { create } from 'zustand'
import {
  DEFAULT_TIME_ZONE,
  ORGANIZATION_TYPE_PROFILES,
  defaultOrganizationSettings,
  type BrandSettings,
  type OrganizationType,
} from '@tumin/domain'
import { api } from '@/lib/api'
import { brandHexColors, derivePalette, paletteToCss } from '@/lib/brand-palette'
import { INTERVALS, STORAGE_KEYS } from '@/lib/constants'
import { FALLBACK_APP_NAME } from '@/lib/env'
import { queryKeys } from '@/lib/query-keys'

/** Respuesta de `GET /public/branding` (disponible sin sesión). */
export interface PublicBranding {
  tradeName: string
  legalName: string
  brand: BrandSettings
  organizationType: OrganizationType
  /** Cómo llama la institución a sus clientes: cliente, asociado, participante. */
  clientNoun: string
  timeZone: string
  /** Llave VAPID para Web Push; `null` si las notificaciones push están apagadas. */
  vapidPublicKey: string | null
}

const defaults = defaultOrganizationSettings()

/** Branding mientras llega la respuesta del API (o si no hay conexión). */
export const DEFAULT_BRANDING: PublicBranding = {
  tradeName: FALLBACK_APP_NAME,
  legalName: defaults.legalName,
  brand: { ...defaults.brand, shortName: FALLBACK_APP_NAME },
  organizationType: defaults.organizationType,
  clientNoun: ORGANIZATION_TYPE_PROFILES[defaults.organizationType].clientNoun,
  timeZone: DEFAULT_TIME_ZONE,
  vapidPublicKey: null,
}

interface BrandingState {
  branding: PublicBranding
  setBranding: (branding: PublicBranding) => void
}

/** Branding vigente (nombre, logo, color, zona horaria). */
export const useBrandingStore = create<BrandingState>((set) => ({
  branding: DEFAULT_BRANDING,
  setBranding: (branding) => set({ branding }),
}))

/** Identificador del `<style>` con la paleta de marca inyectada. */
const BRAND_STYLE_ID = 'tumin-brand'

/**
 * Aplica la paleta derivada del matiz a los tokens CSS, actualiza el color
 * de la barra del navegador y la guarda para que `theme-init.js` la aplique
 * antes del primer pintado en la próxima visita.
 */
export function applyBrandPalette(hue: number): void {
  const css = paletteToCss(derivePalette(hue))
  let style = document.getElementById(BRAND_STYLE_ID) as HTMLStyleElement | null
  if (!style) {
    style = document.createElement('style')
    style.id = BRAND_STYLE_ID
    document.head.appendChild(style)
  }
  style.textContent = css
  updateThemeColorMeta(hue)
  try {
    localStorage.setItem(STORAGE_KEYS.brandCss, css)
  } catch {
    /* almacenamiento bloqueado */
  }
}

/** Sincroniza `<meta name="theme-color">` con el fondo del tema actual. */
export function updateThemeColorMeta(hue: number = useBrandingStore.getState().branding.brand.accentHue): void {
  const colors = brandHexColors(hue)
  const isDark = document.documentElement.classList.contains('dark')
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  if (!meta) {
    meta = document.createElement('meta')
    meta.name = 'theme-color'
    document.head.appendChild(meta)
  }
  meta.content = isDark ? colors.darkBackground : colors.lightBackground
}

/** Carga el branding público y aplica color, título y zona horaria. Montar una vez en la raíz. */
export function useBrandingBootstrap(): void {
  const { data } = useQuery({
    queryKey: queryKeys.publicBranding,
    queryFn: () => api.public.get<PublicBranding>('/public/branding'),
    staleTime: INTERVALS.brandingStaleMs,
  })
  useEffect(() => {
    if (!data) return
    useBrandingStore.getState().setBranding(data)
    applyBrandPalette(data.brand.accentHue)
    document.title = data.tradeName
  }, [data])
}

/** Hook: branding vigente. */
export function useBranding(): PublicBranding {
  return useBrandingStore((s) => s.branding)
}
